import React, { useEffect, useState } from 'react';
import './announcement-layout.css';

export type UpdateBlock = {
  type?: 'heading' | 'text' | 'image' | 'media' | 'button' | 'video' | 'divider' | 'columns';
  text?: string;
  level?: 2 | 3;
  placement?: 'left' | 'right' | 'top' | 'bottom' | null;
  imageUrl?: string | null;
  fullUrl?: string | null;
  imageAlt?: string;
  label?: string;
  url?: string;
  embedUrl?: string | null;
  videoUrl?: string | null;
  posterUrl?: string | null;
  left?: UpdateBlock[];
  right?: UpdateBlock[];
};

type Slide = { src: string; alt: string };

function safeSrc(value?: string | null, folder?: string) {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (folder && !url.pathname.includes(folder)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function videoEmbed(url?: string | null) {
  const safe = safeSrc(url);
  if (!safe) return null;
  try {
    const parsed = new URL(safe);
    const host = parsed.hostname.replace(/^www\./, '').replace(/^m\./, '');
    if (host === 'youtube.com' && parsed.searchParams.get('v')) {
      return `https://www.youtube-nocookie.com/embed/${parsed.searchParams.get('v')}`;
    }
    if (host === 'vimeo.com') {
      const id = parsed.pathname.split('/').filter((part) => /^\d+$/.test(part)).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function collectSlides(blocks?: UpdateBlock[]) {
  const slides: Slide[] = [];
  const walk = (list?: UpdateBlock[]) => {
    (list || []).forEach((block) => {
      const thumb = safeSrc(block.imageUrl, '/storage/');
      const large = safeSrc(block.fullUrl, '/storage/') || thumb;
      if (large && (block.type === 'image' || block.type === 'media' || (!block.type && thumb))) {
        slides.push({ src: large, alt: block.imageAlt || '' });
      }
      walk(block.left);
      walk(block.right);
    });
  };
  walk(blocks);
  return slides;
}

function Picture({
  thumb,
  large,
  alt,
  caption,
  onOpen,
}: {
  thumb: string;
  large: string;
  alt: string;
  caption?: string;
  onOpen: (src: string) => void;
}) {
  return (
    <figure className="aureus-update-figure">
      <button type="button" className="aureus-update-shot" onClick={() => onOpen(large)}>
        <img src={thumb} alt={alt} />
      </button>
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

function ElementView({ block, onOpen }: { block: UpdateBlock; onOpen: (src: string) => void }) {
  const type = block.type || (block.imageUrl || block.text ? 'media' : 'text');
  const thumb = safeSrc(block.imageUrl, '/storage/');
  const large = safeSrc(block.fullUrl, '/storage/') || thumb;
  if (type === 'divider') return <hr className="aureus-update-divider" />;
  if (type === 'heading') {
    const HeadingTag = block.level === 3 ? 'h3' : 'h2';
    return <HeadingTag className={`aureus-update-heading${block.level === 3 ? ' aureus-update-heading--3' : ''}`}>{block.text}</HeadingTag>;
  }
  if (type === 'text') return <p className="aureus-update-copy">{block.text}</p>;
  if (type === 'image' && thumb && large) {
    return <Picture thumb={thumb} large={large} alt={block.imageAlt || ''} caption={block.imageAlt} onOpen={onOpen} />;
  }
  if (type === 'button' && block.url && block.label) {
    const href = safeSrc(block.url);
    if (!href) return null;
    return <a className="aureus-update-link" href={href} target="_blank" rel="noopener noreferrer">{block.label}</a>;
  }
  if (type === 'video') {
    const file = safeSrc(block.videoUrl, '/announcement-videos/');
    if (file) {
      return (
        <div className="aureus-update-video aureus-update-player">
          <video src={file} poster={safeSrc(block.posterUrl, '/announcement-images/') || undefined} controls playsInline preload="metadata" />
        </div>
      );
    }
    const embed = block.embedUrl || videoEmbed(block.url);
    const allowedEmbed = embed && (embed.startsWith('https://www.youtube-nocookie.com/embed/') || embed.startsWith('https://player.vimeo.com/video/'));
    if (!allowedEmbed || !embed) return null;
    return (
      <div className="aureus-update-video">
        <iframe src={embed} title={block.text || 'Update video'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (type === 'columns') {
    return (
      <div className="aureus-update-columns">
        <div className="aureus-update">{(block.left || []).map((child, index) => <ElementView key={`l-${index}`} block={child} onOpen={onOpen} />)}</div>
        <div className="aureus-update">{(block.right || []).map((child, index) => <ElementView key={`r-${index}`} block={child} onOpen={onOpen} />)}</div>
      </div>
    );
  }
  const placement = thumb ? (block.placement || 'top') : 'top';
  return (
    <article className={`aureus-update-block aureus-update-block--${placement}`}>
      {thumb && large ? <Picture thumb={thumb} large={large} alt={block.imageAlt || ''} caption={block.imageAlt} onOpen={onOpen} /> : null}
      {block.text ? <p className="aureus-update-copy">{block.text}</p> : null}
    </article>
  );
}

export const AnnouncementLayout: React.FC<{ blocks?: UpdateBlock[]; fallback?: string }> = ({ blocks, fallback }) => {
  const items = blocks?.length ? blocks : (fallback ? [{ type: 'text' as const, text: fallback }] : []);
  const slides = collectSlides(items);
  const [open, setOpen] = useState<number | null>(null);
  const slide = open === null ? null : slides[open] || null;

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
      if (event.key === 'ArrowRight') setOpen((current) => (current === null ? current : (current + 1) % slides.length));
      if (event.key === 'ArrowLeft') setOpen((current) => (current === null ? current : (current - 1 + slides.length) % slides.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, slides.length]);

  if (!items.length) return null;
  return (
    <>
      <div className="aureus-update">
        {items.map((block, index) => (
          <div key={`${block.type || 'text'}-${index}`} className="aureus-update-item" style={{ animationDelay: `${Math.min(index, 12) * 55}ms` }}>
            <ElementView
              block={block}
              onOpen={(src) => {
                const index = slides.findIndex((item) => item.src === src);
                setOpen(index >= 0 ? index : 0);
              }}
            />
          </div>
        ))}
      </div>
      {slide ? (
        <div className="aureus-lightbox" role="dialog" aria-modal="true" aria-label="Pictures in this update">
          <button type="button" onClick={() => setOpen((current) => (current === null ? current : (current - 1 + slides.length) % slides.length))} aria-label="Previous picture">‹</button>
          <img src={slide.src} alt={slide.alt} />
          <button type="button" onClick={() => setOpen((current) => (current === null ? current : (current + 1) % slides.length))} aria-label="Next picture">›</button>
          <button type="button" className="aureus-lightbox-close" onClick={() => setOpen(null)} aria-label="Close">×</button>
        </div>
      ) : null}
    </>
  );
};

export default AnnouncementLayout;
